import { ToggleGroup } from 'radix-ui';
import { UiLink } from '../labels';
import { cn } from '../lib/cn';
import { Icon, type IconName } from './icon';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Longer accessible name, e.g. "Deutsch" for the label "DE". */
  description?: string;
  icon?: IconName;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  'aria-label': string;
  /**
   * Show only icons; the label stays as accessible name. `'phone'` only below `md`, e.g. a view
   * switch that would take a row of its own on the phone.
   */
  iconOnly?: boolean | 'phone';
  className?: string;
}

const groupClasses = 'inline-flex rounded-md border border-line bg-sunken p-0.5';
const segmentClasses =
  'inline-flex h-control-sm flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-sm px-2.5 text-sm font-medium whitespace-nowrap text-fg-muted transition-colors transition-fast hover:text-fg';
const selectedClasses = 'bg-surface text-fg shadow-raised';

/** Choice between two to four options that applies immediately, e.g. light, dark, system. */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  iconOnly = false,
  className,
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(next) => {
        // Radix allows deselecting; a segmented control always keeps one value.
        if (next) onValueChange(next as T);
      }}
      aria-label={ariaLabel}
      className={cn(groupClasses, className)}
    >
      {options.map((option) => (
        <ToggleGroup.Item
          key={option.value}
          value={option.value}
          aria-label={iconOnly === true ? option.label : option.description}
          title={iconOnly ? option.label : option.description}
          className={cn(
            segmentClasses,
            'data-[state=on]:bg-surface data-[state=on]:text-fg data-[state=on]:shadow-raised',
          )}
        >
          {option.icon && <Icon name={option.icon} />}
          {iconOnly === 'phone' && option.icon ? (
            <span className="max-md:sr-only">{option.label}</span>
          ) : (
            !iconOnly && option.label
          )}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}

export interface ViewSwitchItem {
  href: string;
  label: string;
  icon: IconName;
  /** The view of the current page. */
  active?: boolean;
}

/**
 * Switch between pages that show the same records in different ways, e.g. list and map. Looks
 * like `SegmentedControl` but navigates, so it is a `nav` of links. Icons only on the phone.
 */
export function ViewSwitch({
  label,
  items,
  className,
}: {
  /** Accessible name, e.g. "Darstellung". */
  label: string;
  items: readonly ViewSwitchItem[];
  className?: string;
}) {
  return (
    <nav aria-label={label} className={cn(groupClasses, className)}>
      {items.map((item) => (
        <UiLink
          key={item.href}
          href={item.href}
          aria-current={item.active ? 'page' : undefined}
          className={cn(segmentClasses, item.active && selectedClasses)}
        >
          <Icon name={item.icon} />
          <span className="max-md:sr-only">{item.label}</span>
        </UiLink>
      ))}
    </nav>
  );
}
